/* ─────────────────────────────────────────────────────────────
   --color-* token discovery.

   Names come from the CSSOM, values from the live cascade. That
   split is deliberate: the stylesheets say what EXISTS, so a token
   added to App.css is found without a code change, while
   getComputedStyle says what it currently RESOLVES to, so
   theme-scoped overrides need no special handling.
─────────────────────────────────────────────────────────────── */

import { parseColor, toHex8 } from './colorMath';

const TOKEN_RE = /^--color-[\w-]+$/;

/* Grouping is by name pattern, never by a list of token names, so
   it keeps working as tokens are added. Anything unmatched falls
   into Other — visibly unsorted beats silently missing. */
const GROUP_PATTERNS = [
  { label: 'Brand',          test: /^--color-(primary|teal)/ },
  { label: 'Status',         test: /^--color-(success|warning|error|dirty)/ },
  { label: 'Neutrals',       test: /^--color-(gray|black|bg)/ },
  { label: 'Warm',           test: /^--color-(beige|maroon)/ },
];

const collectFrom = (rules, into) => {
  for (const rule of rules) {
    if (rule.style) {
      for (let i = 0; i < rule.style.length; i += 1) {
        const prop = rule.style[i];
        if (TOKEN_RE.test(prop)) into.add(prop);
      }
    }
    /* @media, @supports and friends nest their own rule lists. */
    if (rule.cssRules) collectFrom(rule.cssRules, into);
  }
};

/** Every --color-* custom property declared anywhere in the page's stylesheets. */
export function discoverTokenNames() {
  if (typeof document === 'undefined') return [];
  const names = new Set();
  for (const sheet of document.styleSheets) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;   // cross-origin sheet — not readable, not ours
    }
    if (rules) collectFrom(rules, names);
  }
  return [...names].sort();
}

/** Resolve one custom property against :root as it currently cascades. */
export const resolveToken = token => {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(token).trim();
};

/**
 * Discovered tokens with their live values and parsed rgba, as
 * [{ token, value, rgba }]. Tokens that resolve to nothing, or to
 * something that isn't a color, are dropped.
 */
export function resolveTokens(names = discoverTokenNames()) {
  return names.reduce((acc, token) => {
    const value = resolveToken(token);
    const rgba = value ? parseColor(value) : null;
    if (rgba) acc.push({ token, value, rgba });
    return acc;
  }, []);
}

/** The same list, bucketed by name pattern for display. */
export function groupTokens(resolved = resolveTokens()) {
  const buckets = new Map(GROUP_PATTERNS.map(g => [g.label, []]));
  const other = [];
  for (const entry of resolved) {
    const group = GROUP_PATTERNS.find(g => g.test.test(entry.token));
    if (group) buckets.get(group.label).push(entry);
    else other.push(entry);
  }
  const out = [...buckets].map(([label, entries]) => ({ label, entries }));
  if (other.length) out.push({ label: 'Other', entries: other });
  return out.filter(g => g.entries.length);
}

/**
 * The token closest to a color, as { token, value, distance, exact },
 * or null when the palette is empty. Distance is plain RGB Euclidean
 * — crude next to a real perceptual metric, but it only has to answer
 * "is this the same color, or near one", and alpha is compared exactly
 * so a translucent overlay never matches its opaque twin.
 */
export function nearestToken(rgba, resolved = resolveTokens()) {
  if (!rgba || !resolved.length) return null;
  const target = toHex8(rgba).toLowerCase();
  let best = null;
  for (const entry of resolved) {
    if (toHex8(entry.rgba).toLowerCase() === target) {
      return { token: entry.token, value: entry.value, distance: 0, exact: true };
    }
    const sameAlpha = (entry.rgba.a ?? 1) === (rgba.a ?? 1);
    const distance = Math.hypot(
      entry.rgba.r - rgba.r,
      entry.rgba.g - rgba.g,
      entry.rgba.b - rgba.b,
    ) + (sameAlpha ? 0 : 255);
    if (!best || distance < best.distance) {
      best = { token: entry.token, value: entry.value, distance, exact: false };
    }
  }
  return best;
}
